import { hasInErrorReason, normalizeDateOfBirth } from "../utils/helpers";
import { isOdsLeaveInError } from "../utils/inErrorRecords";

const VALID_LEAVE_SCHEDULE_TYPES = new Set([
    "Intermittent",
    "Reduced",
    "Continuous",
]);

const PENDING_STATUSES = new Set([
    "Pending",
    "Pending Certification",
    "Time Available",
    "Waiting Period",
]);

const toDisplayDecision = (decision) =>
    PENDING_STATUSES.has(decision) ? "Pending" : decision;

class LeaveDetails {
    _formatClaimLeaveData = (leave) => {
        const employeeDetails = leave?.employeeDetails || {};
        const sourceOrgDetails = leave?.srcOrgDetails || {};
        const leavePeriod = Array.isArray(leave?.leavePeriod)
            ? (leave.leavePeriod[0] ?? {})
            : {};

        return [
            {
                No: leave?.leaveId || "",
                Group: leavePeriod.reportGroupName || "",
                Coverage:
                    leave?.leaveCoverageTypeDesc || leave?.coverageType || "",
                ReceivedDate: leave?.leaveReceivedDt || null,
                Disability: null,
                CurrentStatus: leave?.leaveStatus || "",
                CurrentStatusDate: leave?.leaveStatusEffectiveDt || null,
                Status: leave?.leaveStatusReason || "",
                LastWorkDate: leave?.lastDateWorked || null,
                StartDate: leave?.leaveStartDt || null,
                EndDate: leave?.leaveEndDt || null,
                MaximumDuration: leave?.leaveMaxDuration || null,
                LeaveReason: leave?.leaveReasonDesc || leave?.leaveReason || "",
                RTWDate: leave?.rtwDt || null,
                RTWStatus: leave?.rtwStatus || leave?.RTWStatus || "",
                StatusReason: leave?.leaveCloseReason || "",
                isClaim: false,
                Historical: leave?.historicalLeaveInd === "Y",
                OrganizationId: sourceOrgDetails.orgId,
                EmployeeID: employeeDetails.employeeId || null,
            },
        ];
    };

    _formatEmployeeDetails = (leave) => {
        const employeeDetails = leave?.employeeDetails || {};

        return {
            SSN: employeeDetails.taxIdentificationNumber || null,
            FirstName: employeeDetails.firstName || "",
            MiddleName: employeeDetails.middleName || "",
            LastName: employeeDetails.lastName || "",
            DateOfBirth: normalizeDateOfBirth(employeeDetails.dateOfBirth),
            EmployeeID: employeeDetails.employeeId || null,
        };
    };

    format = (data) => {
        if (!data?.leave?.leaveId || isOdsLeaveInError(data.leave)) {
            return {
                Header: [],
                LeaveSummary: [],
                LeavePeriod: [],
                IntermittentAbsence: [],
                Protection: [],
                AvailableTime: [],
                EmployeeDetails: null,
                ClaimLeaveData: [],
            };
        }

        const leave = data.leave;

        const Header = {
            LeaveNo: leave.leaveId,
            ReportGroup: this._getReportGroupName(leave),
            LeaveReason: leave.leaveReason,
            Historical: leave.historicalLeaveInd,
            LeaveStatus: leave.leaveStatus,
            LeaveHistory: leave.leaveSourceSystem === "NVLNK" ? "NL" : "FN",
            OrganizationId:
                leave.srcOrgDetails?.organizationId ||
                leave.srcOrgDetails?.orgId,
            EmployeeId: leave.employeeDetails?.employeeId,
        };

        const LeaveSummary = {
            ReceivedDate: leave.leaveReceivedDt,
            LastDayWorked: leave.lastDateWorked,
            LeaveStartDate: leave.leaveStartDt,
            LeaveEndDate: leave.leaveEndDt,
            RTWDate: leave.rtwDt,
            LeaveSpecialist: leave.specialistName,
            OfficeLocation: leave.employeeDetails?.employerLocationName,
            ManagerName: leave.employeeDetails?.managerName,
            RTWStatus: leave.rtwStatus,
            RelatedSTDClaims: leave?.relatedClaimIds?.join(","),
        };

        const isNavilink = leave.leaveSourceSystem === "NVLNK";
        const leavePeriods = [];
        const protections = [];
        const availableTime = [];
        const intermittentAbsence = [];

        if (Array.isArray(leave.leavePeriod)) {
            leave.leavePeriod.forEach((period, idx) => {
                if (
                    !VALID_LEAVE_SCHEDULE_TYPES.has(period.leaveScheduleType) ||
                    hasInErrorReason(
                        period.periodStatus,
                        period.periodStatusReason
                    )
                ) {
                    return;
                }

                const periodProtections = Array.isArray(
                    period.leavePeriodProtection
                )
                    ? period.leavePeriodProtection
                    : [];
                const validProtections = periodProtections.filter(
                    (periodProtection) =>
                        !hasInErrorReason(
                            periodProtection.protectionStatusDesc,
                            periodProtection.protectionStatusReasonDesc
                        )
                );

                if (
                    periodProtections.length > 0 &&
                    validProtections.length === 0
                ) {
                    return;
                }

                const leavePeriod = {
                    LeavePeriod: period.leavePeriodNbr,
                    Type: period.leaveScheduleType,
                    HoursOff: period.reducedScheduleHours,
                    Dates: this._getLeavePeriodDates(period),
                    Classification: this._getLeavePeriodClassification(period),
                    Status: period.periodStatus,
                    StatusReason: period.periodStatusReason,
                    LeavePeriodSourceID: idx,
                    Leavedata_LeaveNo: leave.leaveId,
                };

                if (Array.isArray(period.leavePeriodProtection)) {
                    validProtections.forEach((periodProtection) => {
                        protections.push({
                            ProtectionDescription:
                                periodProtection.protectionName,
                            Status: periodProtection.protectionStatusDesc,
                            StatusReason:
                                periodProtection.protectionStatusReasonDesc,
                            Leavedata_LeaveNo: leave.leaveId,
                            LeavePeriodSourceID: idx,
                        });

                        if (periodProtection.protectionHours) {
                            availableTime.push({
                                ProtectionDescription:
                                    periodProtection.protectionName,
                                HoursTaken:
                                    this._getProtectionTimeHours(
                                        periodProtection.protectionHours
                                            ?.hoursTaken
                                    ) ?? "0.00",
                                HoursAvailable:
                                    this._getProtectionTimeHours(
                                        periodProtection.protectionHours
                                            ?.hoursAvailable
                                    ) ?? "0.00",
                            });
                        }
                    });
                }

                const periodIntermittentAbsence = [];

                if (Array.isArray(period.intermittentAbsenceDetails)) {
                    period.intermittentAbsenceDetails.forEach((abs) => {
                        if (
                            !isNavilink &&
                            hasInErrorReason(abs.decisionReasonDesc)
                        ) {
                            return;
                        }

                        const absence = {
                            IntermittentDate: abs.intermittentDt,
                            BeginDateTime: abs.startTime,
                            EndDateTime: abs.endTime,
                            AbsenceTimeMinutes: abs.absenceMinutes,
                            AbsenceTimeHours: abs.absenceHours,
                            ReportedDate: abs.reportedDt,
                            LeavePeriod: period.leavePeriodNbr,
                        };

                        if (!isNavilink) {
                            const matchedProtection = periodProtections.find(
                                (periodProtection) =>
                                    periodProtection.protectionId ===
                                    abs.protectionId
                            );

                            periodIntermittentAbsence.push({
                                ...absence,
                                AbsenceDecision: abs.intermittentStatus,
                                protectionName:
                                    matchedProtection?.protectionName || "",
                            });
                            return;
                        }

                        // Navilink decisions belong to protections, then same-status rows are grouped.
                        if (validProtections.length === 0) {
                            periodIntermittentAbsence.push({
                                ...absence,
                                AbsenceDecision: "",
                                protectionName: "",
                            });
                            return;
                        }

                        validProtections.forEach((periodProtection) => {
                            periodIntermittentAbsence.push({
                                ...absence,
                                AbsenceDecision:
                                    periodProtection.protectionStatusDesc,
                                protectionName:
                                    periodProtection.protectionName || "",
                            });
                        });
                    });
                }

                intermittentAbsence.push(
                    ...this._groupIntermittentAbsence(periodIntermittentAbsence)
                );

                leavePeriods.push(leavePeriod);
            });
        }

        return {
            Header: [Header],
            LeaveSummary: [LeaveSummary],
            LeavePeriod: leavePeriods,
            IntermittentAbsence: intermittentAbsence,
            Protection: protections,
            AvailableTime: this._getUniqueAvailableTime(availableTime),
            EmployeeDetails: this._formatEmployeeDetails(leave),
            ClaimLeaveData: this._formatClaimLeaveData(leave),
        };
    };

    // This method removes duplicate records based on the combination of ProtectionDescription, HoursTaken & HoursAvailable
    // and sorts the results by ProtectionDescription in asc order
    _getUniqueAvailableTime = (records) => {
        return [
            ...new Map(
                records.map((record) => [
                    JSON.stringify([
                        record.ProtectionDescription,
                        record.HoursTaken,
                        record.HoursAvailable,
                    ]),
                    record,
                ])
            ).values(),
        ].sort((a, b) =>
            (a.ProtectionDescription ?? "").localeCompare(
                b.ProtectionDescription ?? ""
            )
        );
    };

    _getReportGroupName = (leave) => {
        if (Array.isArray(leave.leavePeriod) && leave.leavePeriod.length > 0) {
            return leave.leavePeriod[0].reportGroupName;
        }

        return "";
    };

    _groupIntermittentAbsence = (entries) => {
        const grouped = new Map();
        const normalize = (value) =>
            value === null || value === undefined ? "" : String(value).trim();
        const normalizeTime = (value) => {
            if (!value) return "";
            const timestamp = new Date(value).getTime();
            return Number.isNaN(timestamp) ? normalize(value) : timestamp;
        };

        entries.forEach((entry) => {
            const decision = toDisplayDecision(
                normalize(entry.AbsenceDecision)
            );
            const key = JSON.stringify([
                normalizeTime(entry.IntermittentDate),
                normalizeTime(entry.BeginDateTime),
                normalizeTime(entry.EndDateTime),
                decision,
            ]);
            const existing = grouped.get(key);

            if (!existing) {
                grouped.set(key, {
                    ...entry,
                    AbsenceDecision: decision,
                    protectionName: entry.protectionName
                        ? [entry.protectionName]
                        : [],
                });
                return;
            }

            if (
                entry.protectionName &&
                !existing.protectionName.includes(entry.protectionName)
            ) {
                existing.protectionName.push(entry.protectionName);
            }
        });

        return Array.from(grouped.values(), (entry) => ({
            ...entry,
            protectionName: entry.protectionName
                .sort((a, b) => a.localeCompare(b))
                .join(", "),
        }));
    };

    _getProtectionTimeHours = (hrs) => {
        if (hrs === 0.0) {
            return "0.00";
        } else if (typeof hrs === "number") {
            return hrs;
        }

        return null;
    };

    _getLeavePeriodDates = (period) => {
        let dates = "";

        if (period.periodStartDt) {
            dates += `${period.periodStartDt}`;
        }

        if (period.periodEndDt) {
            dates += ` - ${period.periodEndDt}`;
        }

        return dates;
    };

    _getLeavePeriodClassification = (period) => {
        if (Array.isArray(period.leavePeriodClassifications)) {
            return period.leavePeriodClassifications
                .filter((c) => c.leavePeriodClassificationTypeDesc !== "")
                .map((c) => c.leavePeriodClassificationTypeDesc)
                .join(" / ");
        }

        return "";
    };
}

export default new LeaveDetails();
